import {
  CommandProps,
  ExtendedRegExpMatchArray,
  InputRule,
  InputRuleFinder,
  Node,
  mergeAttributes,
  callOrReturn,
} from '@tiptap/core';
import { MarkdownNodeSpec } from 'tiptap-markdown';
import texmath, { RuleName } from './texmath';
import { createMathView } from './math-plugin';
import { NodeType } from 'prosemirror-model';
import { TextSelection } from 'prosemirror-state';
import { MathToken } from '@scale/shared/types/config';

export const dollarInputRegex = /^\$\$?[\s\n]$/;
export const bracketNodeInputRegex = /(?<!\\)\\\[[\s\n]$/;
export const escapedBracketNodeInputRegex = /\\\\\[[\s\n]$/;

type MathBlockOptions = { allowedRules: RuleName[] };

type MathNodeAttributes = {
  open: string;
  close: string;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    math_display: {
      setFormulaBlock: (attributes: MathNodeAttributes) => ReturnType;
      toggleFormulaBlock: () => ReturnType;
      unsetFormulaBlock: () => ReturnType;
    };
  }
}

function getNodeAttributes(type: MathToken): MathNodeAttributes {
  switch (type) {
    case MathToken.DOLLARS:
      return {
        open: '$$',
        close: '$$',
      };
    case MathToken.BRACKETS:
      return {
        open: '\\[',
        close: '\\]',
      };
    case MathToken.ESCAPED_BRACKETS:
      return {
        open: '\\\\[',
        close: '\\\\]',
      };
  }
}

function nodeInputRule(config: {
  /**
   * The regex to match.
   */
  find: InputRuleFinder;

  /**
   * The node type to add.
   */
  type: NodeType;

  /**
   * A function that returns the attributes for the node
   * can also be an object of attributes
   */
  getAttributes?:
    | Record<string, any>
    | ((match: ExtendedRegExpMatchArray) => Record<string, any>)
    | false
    | null;
}) {
  return new InputRule({
    find: config.find,
    handler: ({ state, range, match }) => {
      const attributes = callOrReturn(config.getAttributes, undefined, match) || {};
      const { tr } = state;
      const start = range.from;
      let end = range.to;

      const newNode = config.type.create(attributes);

      if (match[1]) {
        const offset = match[0].lastIndexOf(match[1]);
        let matchStart = start + offset;

        if (matchStart > end) {
          matchStart = end;
        } else {
          end = matchStart + match[1].length;
        }

        // insert last typed character
        const lastChar = match[0][match[0].length - 1];

        tr.insertText(lastChar, start + match[0].length - 1);

        // insert node from input rule
        tr.replaceWith(matchStart, end, newNode);

        // Place the cursor inside the new node
        if (newNode.isTextblock) {
          tr.setSelection(TextSelection.create(tr.doc, matchStart)); // For block nodes
        } else if (newNode.isInline) {
          tr.setSelection(TextSelection.create(tr.doc, matchStart)); // For inline nodes
        }
      } else if (match[0]) {
        const insertionStart = config.type.isInline ? start : start - 1;
        const node = config.type.create(attributes);

        tr.insert(insertionStart, node).delete(tr.mapping.map(start), tr.mapping.map(end));
        // Place the cursor inside the inserted node
        if (node.isTextblock) {
          tr.setSelection(TextSelection.create(tr.doc, insertionStart)); // For block nodes
        } else if (node.isInline) {
          tr.setSelection(TextSelection.create(tr.doc, insertionStart)); // For inline nodes
        }
      }

      tr.scrollIntoView();
    },
  });
}

export default Node.create<MathBlockOptions>({
  name: 'math_display',
  group: 'block math',
  content: 'text*', // important!
  atom: true, // important!
  code: true,

  addOptions() {
    return {
      allowedRules: [MathToken.DOLLARS],
    };
  },

  addStorage(): { markdown: MarkdownNodeSpec } {
    return {
      markdown: {
        serialize(state, node) {
          state.text(`${node.attrs.open}`, false);
          state.text(`${node.textContent}`, false);
          state.text(`${node.attrs.close}`, false);
          state.ensureNewLine();
        },
        parse: {
          setup(markdownIt) {
            const delimiters = this.options.allowedRules;
            markdownIt.use(texmath as any, {
              delimiters,
              rules: 'block',
            });
          },
        },
      },
    };
  },

  addAttributes() {
    return {
      open: {
        default: '$$',
        renderHTML: attributes => {
          return {
            open: attributes.open,
          };
        },
      },
      close: {
        default: '$$',
        renderHTML: attributes => {
          return {
            close: attributes.close,
          };
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'math-display', // important!
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return ['math-display', mergeAttributes(HTMLAttributes, { class: 'math-node' }), 0];
  },

  addInputRules() {
    const rules: RuleName[] = this.options.allowedRules;
    const inputRules: Record<RuleName, InputRule> = {
      [MathToken.DOLLARS]: nodeInputRule({
        find: dollarInputRegex,
        type: this.type,
        getAttributes: {
          open: '$$',
          close: '$$',
        },
      }),
      [MathToken.ESCAPED_BRACKETS]: nodeInputRule({
        find: escapedBracketNodeInputRegex,
        type: this.type,
        getAttributes: {
          open: '\\\\[',
          close: '\\\\]',
        },
      }),
      [MathToken.BRACKETS]: nodeInputRule({
        find: bracketNodeInputRegex,
        type: this.type,
        getAttributes: {
          open: '\\[',
          close: '\\]',
        },
      }),
      gitlab: nodeInputRule({
        find: /```math(.+)```/,
        type: this.type,
        getAttributes: {
          open: '```math',
          close: '```',
        },
      }),
    };

    return rules.map(ruleName => inputRules[ruleName]);
  },

  addCommands() {
    return {
      setFormulaBlock:
        (attributes: MathNodeAttributes) =>
        ({ commands }: CommandProps) => {
          return commands.setNode(this.name, attributes);
        },
      toggleFormulaBlock:
        () =>
        ({ commands }) => {
          const defaultNodeType =
            this.editor.storage.markdownExtensions.mathToken ?? MathToken.DOLLARS;
          return commands.toggleNode(this.name, 'paragraph', getNodeAttributes(defaultNodeType));
        },
      unsetFormulaBlock:
        () =>
        ({ commands }: CommandProps) => {
          return commands.unsetMark(this.name);
        },
    };
  },

  addNodeView() {
    return ({ node, getPos }) => {
      const nodeViewRenderer = createMathView(true);
      return nodeViewRenderer(node, this.editor.view, getPos);
    };
  },
});
